import type { Tier } from './types'

interface BadgeMedallionProps {
  tier: Tier
  iconUrl?: string | null
  /** Nome curto exibido no centro quando o ícone não está carregado. */
  inscription?: string
  /** Tamanho lateral em px. Default 200. */
  size?: number
  className?: string
  /**
   * Exibe a inscrição (nome + tier) no centro da bola do fallback.
   * Default `true`. Passe `false` quando o nome já aparece fora da
   * medalha (ex.: badges bloqueadas no BadgesCard) — evita texto
   * redundante e poluído atrás do cadeado.
   */
  showInscription?: boolean
}

const tierLabel: Record<Tier, string> = {
  bronze: 'BRONZE',
  prata: 'PRATA',
  ouro: 'OURO',
  platina: 'PLATINA',
}

/**
 * Medalha do sistema de badges.
 *
 * Dois modos de render conforme `iconUrl`:
 *
 * 1. **Com iconUrl** — Mostra apenas o PNG enviado pelo super_admin,
 *    sem o gradiente metálico atrás. A identidade da badge vem do
 *    desenho do design (FCamara), não da decoração genérica. O PNG é
 *    transparente (alpha preservado no upload) e ocupa o tamanho
 *    integral do componente. Mantém aria-label do tier pra acessibilidade.
 *
 * 2. **Sem iconUrl** (fallback) — Renderiza a "bola" metálica com
 *    gradiente radial + reflexo + inscrição. Usado em badges
 *    cadastradas sem upload ainda, ou em estado bloqueado pra dar
 *    silhueta cinza (BadgesCard passa iconUrl=null nesse caso).
 */
export function BadgeMedallion({
  tier,
  iconUrl,
  inscription,
  size = 200,
  className = '',
  showInscription = true,
}: BadgeMedallionProps) {
  // Modo "PNG puro" — quando o design entregou o ícone, ele É a badge.
  // Sem wrapper metálico, sem inscription, sem decoração extra.
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt={`Medalha ${tierLabel[tier]}`}
        className={className}
        style={{
          width: size,
          height: size,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    )
  }

  // Fallback decorativo — quando não há ícone enviado, mostra a bola
  // metálica com a inscrição. É o que se vê em badges recém-cadastradas
  // (sem PNG ainda) ou em badges bloqueadas (BadgesCard passa null).
  const safeInscription = (inscription || 'NOVA').slice(0, 18).toUpperCase()
  const nameFontSize = Math.max(11, Math.round(size * 0.13))

  return (
    <div
      className={`bdg-medallion ${className}`}
      data-tier={tier}
      style={{ width: size, height: size }}
      aria-label={`Medalha ${tierLabel[tier]}`}
    >
      {showInscription && (
        <div className="bdg-medallion-content">
          <span className="bdg-medallion-name" style={{ fontSize: nameFontSize }}>
            {safeInscription}
          </span>
          <span className="bdg-medallion-tier">◆ {tierLabel[tier]} ◆</span>
        </div>
      )}
    </div>
  )
}
